import React from "react";
import i18next, { useTranslation } from "utilities/i18n";
import { useSelector } from "react-redux";
import { State } from "store";
import { FilterPanel } from "collections/FilterPanel";
import { Section } from "collections/Section";
import {
  findRelativeTimePeriod,
  indyTrending,
  tMetric
} from "collections/InsightDetails";
import {
  AreaChart,
  ChartData,
  FeatureFlagEnabled,
  FeatureFlagsOrEnabled,
  Loader,
  LoadingIndicator,
  PremiumBetaBadge,
  SimpleBarChart,
  XRangeChart
} from "elements";
import {
  Category,
  InsightDetailsFragment,
  LocationScope,
  NodeDetailsQueryVariables,
  SimilarInsightsFragment,
  SimilarTopicIndicatorsQueryVariables,
  TimeScope,
  useChartsQuery,
  useNodeDetailsQuery,
  useSimilarTopicIndicatorsQuery
} from "generated/graphql";
import {
  generatePath,
  Link,
  Redirect,
  useParams,
  useQueryParam,
  useRouteMatch,
  useSelectedTenants
} from "polyrouter";
import InsightBreakdownList from "./InsightBreakdownList/InsightBreakdownList";
import { truncDecimals } from "utilities/numberUtils";
import {
  getInsightDescriptionTranslation,
  getInsightTitleTranslation
} from "utilities/insights";
import { Layout } from "views/Main/Layouts";
import { getDataGroupByParam } from "../Priority/PriorityInsights";
import {
  InsightDetailsCard,
  InsightDetailsCardHeader
} from "./InsightDetailsCard";
import "./PriorityInsightsDetails.scss";
import { SimilarInsightsCard } from "./SimilarInsightsCard";
import { OperationContext } from "@urql/core";
import { colorsForChart, determineChartType } from "utilities/chart";
import { THEME_DEFAULT } from "store/userMeta";

type LocationScopeKey =
  | "DEVICE"
  | "DEVICE_TYPE"
  | "FAMILY"
  | "MODEL"
  | "MODEL_VERSION"
  | "ROOM"
  | "ROOM_MEETING"
  | "SITE"
  | "TENANT"
  | "VERSION";

type LocationScopeOtherKey =
  | "DEVICE_other"
  | "DEVICE_TYPE_other"
  | "FAMILY_other"
  | "MODEL_other"
  | "MODEL_VERSION_other"
  | "ROOM_other"
  | "ROOM_MEETING_other"
  | "SITE_other"
  | "TENANT_other"
  | "VERSION_other"
  | "UNKNOWN_other";

export const parseLegacyInsightsBarChartData = (
  data: InsightDetailsFragment
): Array<ChartData> => {
  return [
    {
      figure:
        data.indicator.__typename === "CountOfTotalIndicator"
          ? data.indicator.percentage
          : data.indicator.average,
      desc: getDataGroupByParam(data) ?? i18next.t("unknown")
    },
    {
      figure: data.tenantAverage,
      desc: i18next.t("TENANT")
    },
    {
      figure: data.globalAverage,
      desc: i18next.t("GLOBAL")
    }
  ];
};

const parsePercentiles = (figure: number) => {
  const perc = truncDecimals(figure * 100);

  if (perc < 11 || perc > 13) {
    switch (perc % 10) {
      case 1:
        return `${perc}st`;
      case 2:
        return `${perc}nd`;
      case 3:
        return `${perc}rd`;
      default:
        return `${perc}th`;
    }
  }

  return `${perc}th`;
};

const parseIndicatorCount = (
  count: string,
  indicatorType: string,
  name: string = ""
) => {
  // need units for average indicators
  if (indicatorType !== "CountOfTotalIndicator") {
    // TODO: This feels very brittle.  Possibly some fuzzy / includes for keywords?
    if (
      name.toLowerCase() === "people per meeting" ||
      name.toLowerCase() === "average meeting size"
    ) {
      return `${count} people`;
    }

    return `${count} minutes`;
  }

  return count;
};

const previousInsightTranslationOptions = (data: InsightDetailsFragment) => {
  const mt = tMetric();
  const period = findRelativeTimePeriod(data.timePeriodStart, data.timeScope);
  return {
    site: getDataGroupByParam(data),
    name: getInsightTitleTranslation(data.topic.id).toLowerCase(),
    period: data.timeScope === TimeScope.Day ? period : period.toLowerCase(),
    previous: parseIndicatorCount(
      mt(data.previous),
      data.indicator.__typename,
      getInsightTitleTranslation(data.topic.id)
    )
  };
};

const filterByLocationScope = (
  input: Array<ChartData>,
  locationScope: LocationScope
) => {
  if (locationScope === LocationScope.Tenant) {
    const excludeScopeLabel = i18next.t("TENANT");
    return input.filter(entry => entry.desc !== excludeScopeLabel);
  }
  return input;
};

const generateAverageAndPercentileString = (
  data: InsightDetailsFragment,
  desc: "company" | "global"
) => {
  const mt = tMetric();
  const translationOptions = {
    site: getDataGroupByParam(data),
    tenantAverage:
      data.indicator.__typename === "CountOfTotalIndicator"
        ? mt({
            __typename: "CountOfTotalIndicator",
            percentage: data.tenantAverage,
            count: 0,
            total: 0
          })
        : mt({
            __typename: "AverageIndicator",
            average: data.tenantAverage
          }),
    tenantPercentile: parsePercentiles(data.tenantPercentile),
    globalAverage:
      data.indicator.__typename === "CountOfTotalIndicator"
        ? mt({
            __typename: "CountOfTotalIndicator",
            percentage: data.globalAverage,
            count: 0,
            total: 0
          })
        : mt({
            __typename: "AverageIndicator",
            average: data.globalAverage
          }),
    globalPercentile: parsePercentiles(data.globalPercentile)
  };
  switch (desc) {
    case "global":
      return i18next.t("GLOBAL_AVERAGE_AND_PERCENTILE", translationOptions);
    case "company":
      return i18next.t("COMPANY_AVERAGE_AND_PERCENTILE", translationOptions);
    default:
      return "";
  }
};

const generateTrendingString = (
  data: InsightDetailsFragment,
  trending: number
) => {
  const period = findRelativeTimePeriod(data.timePeriodStart, data.timeScope);
  const translationOptions = {
    site: getDataGroupByParam(data),
    name: getInsightTitleTranslation(data.topic.id).toLowerCase(),
    time: data.timeScope === TimeScope.Day ? period : period.toLowerCase()
  };
  // we've already eliminated the case where previous==current / undefined

  const transKey = `${
    trending > 0 ? "TRENDING_UP" : "TRENDING_DOWN"
  }_DESC` as const;
  return i18next.t(transKey, translationOptions);
};

const useChartsQueryForInsight = (insight: InsightDetailsFragment) => {
  const id = (() => {
    switch (insight.locationScope) {
      case LocationScope.Device:
        return insight.device?.id;
      case LocationScope.DeviceType:
        return insight.deviceType;
      case LocationScope.Family:
        return insight.family;
      case LocationScope.Model:
        return insight.model;
      case LocationScope.ModelVersion:
        return insight.modelVersion;
      case LocationScope.Room:
        return insight.room?.id;
      case LocationScope.Site:
        return insight.site?.id;
      case LocationScope.Tenant:
        return insight.tenant.id;
      case LocationScope.Version:
        return insight.version;
      default:
        return null;
    }
  })();

  return useChartsQuery({
    variables: {
      id: id ?? "",
      tenantId: [insight.tenant.id],
      locationScope: insight.locationScope,
      timeScope: insight.timeScope,
      timeStart: insight.timePeriodStart,
      topic: insight.topic.id
    }
  });
};

const Charts: React.FC<{
  insight: InsightDetailsFragment;
}> = ({ insight }) => {
  const theme = useSelector((state: State) => {
    return state.userMeta?.theme ?? THEME_DEFAULT;
  });
  const { data, fetching } = useChartsQueryForInsight(insight);

  /** Check chart data type and return if not insight chart data */
  if (data?.charts?.meta.__typename === "MeetingRecordAggregateChartMeta") {
    return null;
  }

  const chartType = determineChartType(data?.charts?.meta);
  const chartColors = colorsForChart(
    chartType,
    data?.charts?.series.length,
    theme
  );

  return (
    <InsightDetailsCard className="insights-chart-card">
      {fetching ? (
        <LoadingIndicator />
      ) : "xrange" === chartType ? (
        <XRangeChart
          colors={chartColors}
          categories={["In Call"]}
          series={data?.charts?.series}
        />
      ) : (
        <AreaChart
          series={data?.charts?.series}
          timeScope={data?.charts?.meta.timeScope}
          theme={theme}
          colors={chartColors}
          title={getInsightTitleTranslation(insight.topic.id)}
        />
      )}
    </InsightDetailsCard>
  );
};

const InsightBreakdown: React.FC<{
  data: InsightDetailsFragment;
}> = ({ data }) => {
  // FIXME: Became a type error on 4.x+
  // @ts-ignore
  return <InsightBreakdownList data={data.recommendedDetail} />;
};

const DetailsSection: React.FC<{
  data: InsightDetailsFragment;
}> = ({ data }) => {
  const { t } = useTranslation();
  const trending = indyTrending(data.previous, data.indicator);

  return (
    <>
      <InsightDetailsCard>
        <div className="grid fitted" data-testid="details-card">
          <div className="col-6">
            <InsightDetailsCardHeader
              subHeader={findRelativeTimePeriod(
                data.timePeriodStart,
                data.timeScope
              )}
            >
              {getInsightTitleTranslation(data.topic.id)}
            </InsightDetailsCardHeader>
            <p>{generateAverageAndPercentileString(data, "company")}</p>
            <p>{generateAverageAndPercentileString(data, "global")}</p>
          </div>
          <div className="col-6">
            <SimpleBarChart
              data={
                data
                  ? filterByLocationScope(
                      parseLegacyInsightsBarChartData(data),
                      data.locationScope
                    )
                  : []
              }
              percentage={data.indicator.__typename === "CountOfTotalIndicator"}
            />
          </div>
        </div>
      </InsightDetailsCard>
      <div className="grid fitted">
        {trending ? (
          <div className="col-6">
            <InsightDetailsCard className="details-card">
              <InsightDetailsCardHeader
                icon={trending > 0 ? "trend_up" : "trend_down"}
              >
                {trending > 0 ? t("ON_THE_RISE") : t("TRENDING_DOWN")}
              </InsightDetailsCardHeader>
              <p>{generateTrendingString(data, trending)}</p>
            </InsightDetailsCard>
          </div>
        ) : null}
        {data.previous && (
          <div
            className={trending ? "col-6" : "col-12"}
            data-testid="previous-card"
          >
            <InsightDetailsCard className="details-card">
              <InsightDetailsCardHeader icon="calendar_info">
                {t("PREVIOUS_PERIOD", {
                  period: findRelativeTimePeriod(
                    data.timePeriodStart,
                    data.timeScope
                  )
                })}
              </InsightDetailsCardHeader>
              <p>
                {t(
                  "PREVIOUS_PERIOD_DESC",
                  previousInsightTranslationOptions(data)
                )}
              </p>
            </InsightDetailsCard>
          </div>
        )}
      </div>
    </>
  );
};

// IMPORTANT NOTE: Why isn't this inline in the useQuery? Because it causes an infinite loop.....
const similarInsightsContext = {
  notify: {
    i18nKey: "SIMILAR_PRIORITY_INSIGHTS_LOADING_ERROR"
  }
};

const SimilarInsightsSection: React.FC<{
  mainInsight: InsightDetailsFragment;
  tenant: Array<string>;
}> = ({ mainInsight, tenant }) => {
  let variables: SimilarTopicIndicatorsQueryVariables = {
    tenant: tenant,
    input: {
      topic: [mainInsight.topic.id]
    }
  };
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { Header } = Section;
  const { data, fetching } = useSimilarTopicIndicatorsQuery({
    variables,
    context: similarInsightsContext
  });

  if (!data) {
    return null;
  }

  const nodes = data.similar.edges
    .filter(edge => {
      if (edge.node.id !== id) {
        return edge;
      }
      return null;
    })
    .slice(0, 10)
    .map(edge => edge.node);
  return nodes.length > 0 ? (
    <>
      <Header>{t("SIMILAR_PRIORITY_INSIGHTS")}</Header>
      <div className="similar-insights">
        <Loader loading={fetching}>
          {nodes.map((entry: SimilarInsightsFragment) => (
            <Link
              data-testid={"similar-insights-card-testid"}
              key={entry.id}
              persist={["InsightsFilterState"]}
              to={`/insights/details/${encodeURIComponent(entry.id)}`}
            >
              <SimilarInsightsCard data={entry} loading={fetching} />
            </Link>
          ))}
        </Loader>
      </div>
    </>
  ) : null;
};

const PriorityInsightDetailsSidebar: React.FC<{
  data: InsightDetailsFragment;
}> = ({ data }) => {
  const mt = tMetric();

  const { Sidebar } = Layout;
  return (
    <Sidebar data-testid="sidebar">
      <FilterPanel
        filters={[]}
        metric={{
          type: getInsightTitleTranslation(data.topic.id),
          count: data ? mt(data.indicator) : 0
        }}
        subHeader={getInsightDescriptionTranslation(data.topic.id)}
      />
    </Sidebar>
  );
};

const InsightDetailsPageHeader: React.FC<{
  mainInsight: InsightDetailsFragment;
}> = ({ mainInsight }) => {
  const { Header } = Section;
  const { t } = useTranslation();

  const locationScope: LocationScopeKey = mainInsight.locationScope;

  return (
    <Header className="mb-1 d-flex">
      {t("INSIGHT_DETAILS_PAGE_HEADER", {
        locationScope: t(locationScope),
        scopeParam: getDataGroupByParam(mainInsight)
      })}
      <div className="page-subheader">
        &nbsp;|&nbsp;
        {t("INSIGHT_DETAILS_PAGE_SUBHEADER", {
          timeRange: findRelativeTimePeriod(
            mainInsight.timePeriodStart,
            mainInsight.timeScope
          )
        })}
      </div>
      {mainInsight.topic.category === Category.Room && <PremiumBetaBadge />}
    </Header>
  );
};

//Insight data hardly changes, if we ever call this query once, we want to just hit the cache.
const favorCache: Partial<OperationContext> = {
  requestPolicy: "cache-first"
};

const useInsightsQuery = (id: string) => {
  const tenants = useSelectedTenants();
  const variables: NodeDetailsQueryVariables = {
    id: id ? decodeURIComponent(id) : "",
    tenant: tenants
  };
  const { data, fetching, error } = useNodeDetailsQuery({
    variables,
    context: favorCache
  });
  let mainInsight: InsightDetailsFragment | null = null;
  let deviceCount = 0;

  if (data?.nodeDetail?.__typename === "TopicIndicator") {
    mainInsight = data.nodeDetail;
  }

  switch (mainInsight?.indicator.__typename) {
    case "CountOfTotalIndicator":
      deviceCount = mainInsight?.indicator.count;
      break;
    case "CountAverageIndicator":
      deviceCount = mainInsight?.indicator.count;
      break;
    case "AverageIndicator":
      break;
  }

  return { mainInsight, fetching, error, deviceCount };
};

const PriorityInsightDetails: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const tenants = useSelectedTenants();
  const { mainInsight } = useInsightsQuery(id);
  const [match] = useQueryParam("r");
  const { Content } = Layout;
  const { Header, Content: SContent } = Section;
  const to = generatePath("/insights/:path", {
    path: match ? "categories" : "priority"
  });

  if (!mainInsight) {
    return null;
  }

  const locationScope =
    mainInsight.recommendedDetail?.locationScope || "UNKNOWN";
  const locationKey: LocationScopeOtherKey = `${locationScope}_other` as const;

  return (
    <Layout
      back={{
        to: { pathname: to, state: id },
        persist: ["InsightsFilterState"],
        iconDesc: t("RETURN_TO_PRIORITY"),
        title: t("BACK")
      }}
    >
      <PriorityInsightDetailsSidebar data={mainInsight} />
      <Content noPageTitle data-testid="test" scrolling>
        <Content.ScrollingList>
          <InsightDetailsPageHeader mainInsight={mainInsight} />
          <FeatureFlagsOrEnabled
            feature="INSIGHTS_CHARTS"
            orFeature="IS_POLY_USER"
          >
            <Section>
              <Header>{t("PERFORMANCE")}</Header>
              <SContent>
                <Charts insight={mainInsight} />
              </SContent>
            </Section>
          </FeatureFlagsOrEnabled>
          <FeatureFlagEnabled
            feature="INSIGHTS_DEVICE_LIST"
            andIf={!!mainInsight.recommendedDetail}
          >
            <Section>
              <Header>{t("INSIGHT_BREAKDOWN")}</Header>
              <div className="scope-switcher mt-n5 mb-1 pb-5">
                {t(locationKey)}
              </div>
              <SContent>
                <InsightBreakdown data={mainInsight} />
              </SContent>
            </Section>
          </FeatureFlagEnabled>
          <FeatureFlagEnabled feature="INSIGHTS_LEGACY">
            <>
              <Section>
                <Header>{t("DETAILS")}</Header>
                <SContent>
                  <DetailsSection data={mainInsight} />
                </SContent>
              </Section>
              <Section>
                <SContent>
                  {tenants && (
                    <SimilarInsightsSection
                      mainInsight={mainInsight}
                      tenant={tenants}
                    />
                  )}
                </SContent>
              </Section>
            </>
          </FeatureFlagEnabled>
        </Content.ScrollingList>
      </Content>
    </Layout>
  );
};

const PriorityDetailsLoader: React.FC = () => {
  const {
    params: { id }
  } = useRouteMatch<{ id: string }>();
  const { mainInsight, fetching } = useInsightsQuery(id);

  if (fetching && !mainInsight) {
    return <LoadingIndicator />;
  }

  if (!fetching && !mainInsight) {
    return <Redirect to="/insights" />;
  }

  return <PriorityInsightDetails id={id} />;
};

export default PriorityDetailsLoader;
